/*
  Thirsty Pot header behaviours.

  Two disclosures, both progressive enhancement over markup that is already
  usable without JS: the desktop "Shop" mega menu and the mobile nav drawer.
  The trigger button carries aria-expanded and aria-controls; this element just
  wires up toggling, outside-click and Escape.
*/

if (!customElements.get('tp-disclosure')) {
  customElements.define(
    'tp-disclosure',
    class TpDisclosure extends HTMLElement {
      connectedCallback() {
        this.trigger = this.querySelector('[data-tp-disclosure-trigger]');
        this.panelId = this.trigger && this.trigger.getAttribute('aria-controls');
        this.panel = this.panelId ? document.getElementById(this.panelId) : null;

        if (!this.trigger || !this.panel) return;

        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);

        this.trigger.addEventListener('click', this.onTriggerClick);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
      }

      get open() {
        return this.trigger.getAttribute('aria-expanded') === 'true';
      }

      setOpen(value) {
        this.trigger.setAttribute('aria-expanded', String(value));
        this.panel.toggleAttribute('hidden', !value);
      }

      onTriggerClick(event) {
        event.preventDefault();
        this.setOpen(!this.open);
      }

      onDocumentClick(event) {
        if (!this.open) return;
        // The panel is rendered outside this element for the full-bleed mega
        // menu, so test both before closing.
        if (this.contains(event.target) || this.panel.contains(event.target)) return;
        this.setOpen(false);
      }

      onKeydown(event) {
        if (event.key !== 'Escape' || !this.open) return;
        this.setOpen(false);
        this.trigger.focus();
      }
    }
  );
}

/*
  Product gallery: thumbnail rail drives the main frame, and the main frame
  opens a <dialog> lightbox. Every frame is rendered up front and toggled with
  `hidden`, so switching never waits on a network request.
*/
if (!customElements.get('tp-gallery')) {
  customElements.define(
    'tp-gallery',
    class TpGallery extends HTMLElement {
      connectedCallback() {
        this.frames = [...this.querySelectorAll('[data-tp-frame]')];
        this.thumbs = [...this.querySelectorAll('[data-tp-thumb]')];
        this.counter = this.querySelector('[data-tp-counter]');
        this.dialog = this.querySelector('[data-tp-lightbox]');
        this.lightImg = this.querySelector('[data-tp-lightbox-img]');
        this.caption = this.querySelector('[data-tp-caption]');
        this.index = 0;

        if (!this.frames.length) return;

        this.thumbs.forEach((t) =>
          t.addEventListener('click', () => this.show(Number(t.dataset.tpThumb)))
        );

        const open = this.querySelector('[data-tp-open]');
        if (open && this.dialog) open.addEventListener('click', () => this.open());

        this.querySelector('[data-tp-close]')?.addEventListener('click', () => this.dialog.close());
        this.querySelector('[data-tp-prev]')?.addEventListener('click', () => this.step(-1));
        this.querySelector('[data-tp-next]')?.addEventListener('click', () => this.step(1));

        // Clicking the backdrop closes; clicking the image itself should not.
        this.dialog?.addEventListener('click', (e) => {
          if (e.target === this.dialog) this.dialog.close();
        });

        this.dialog?.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') this.step(-1);
          if (e.key === 'ArrowRight') this.step(1);
        });
      }

      show(i) {
        if (i < 0 || i >= this.frames.length) return;
        this.index = i;
        this.frames.forEach((f, n) => f.toggleAttribute('hidden', n !== i));
        this.thumbs.forEach((t, n) => {
          if (n === i) t.setAttribute('aria-current', 'true');
          else t.removeAttribute('aria-current');
        });
        if (this.counter) this.counter.textContent = `${i + 1} / ${this.frames.length}`;
        if (this.dialog?.open) this.paint();
      }

      step(d) {
        const next = (this.index + d + this.frames.length) % this.frames.length;
        this.show(next);
      }

      paint() {
        const img = this.frames[this.index].querySelector('img');
        if (!img || !this.lightImg) return;
        this.lightImg.src = img.currentSrc || img.src;
        this.lightImg.alt = img.alt || '';
        if (this.caption) {
          this.caption.textContent = `${img.alt || ''} · ${this.index + 1} / ${this.frames.length}`;
        }
      }

      open() {
        if (!this.dialog) return;
        this.paint();
        this.dialog.showModal();
      }
    }
  );
}
